import { label, projectDate, shortDate, timezoneOffset } from '@entities/ksp/format';
import type { KspWorkspace, ScheduleVersion } from '@entities/ksp/types';
import { Button } from '@shared/ui/Button';
import {
  BackLink,
  Card,
  Chip,
  Count,
  HeaderBar,
  Notice,
  SectionLink,
  SectionNav,
  Small,
  Spacer,
  SubLink,
  SubNav,
} from '../styles';
import { SECTIONS, sectionOf, tabHref, tabTitle } from '../types';

export interface WorkspaceHeaderProps {
  workspace: KspWorkspace;
  timezone: string;
  version?: ScheduleVersion;
  isFetching: boolean;
  canWrite: boolean;
  tab: string;
  params: URLSearchParams;
  message?: string;
  errors: string[];
  error?: unknown;
  onRefetch: () => void;
  onExport: () => void;
}

export function WorkspaceHeader({
  workspace: d,
  timezone,
  version,
  isFetching,
  canWrite,
  tab,
  params,
  message,
  errors,
  error,
  onRefetch,
  onExport,
}: WorkspaceHeaderProps) {
  const active = sectionOf(tab);
  const openAlerts = d.operational?.open_milestone_alert_count ?? 0;
  // Права на изменения в КСП выдаются отдельно на каждый проект (ksp.action_grants):
  // роль с правом записи без них видит проект только для просмотра.
  const noGrants = canWrite && !d.history_read_only && (d.grants ?? []).length === 0;
  const calcTitle = d.summary
    ? [
        `Дата состояния: ${projectDate(d.summary.data_date, timezone)}`,
        `Расчёт: ${projectDate(d.summary.calculated_at, timezone)}`,
        `Время объекта: ${timezoneOffset(timezone)} (${timezone})`,
        d.operational
          ? `Оперативные данные: ${projectDate(d.operational.operational_as_of, timezone)}, ревизия ${d.operational.revision}`
          : '',
      ]
        .filter(Boolean)
        .join('\n')
    : `Время объекта: ${timezoneOffset(timezone)} (${timezone})`;
  return (
    <>
      <BackLink to="/agents/ksp">← Проекты КСП</BackLink>
      <HeaderBar>
        <h1>{d.project_name}</h1>
        {version ? (
          <Chip title="Выбранная версия графика">
            {label(version.kind)} · ред. {version.revision} · {label(version.approval)}
          </Chip>
        ) : (
          <Chip $tone="warn">Версия графика не выбрана</Chip>
        )}
        <Chip title={calcTitle}>
          {d.summary ? `Расчёт ${shortDate(d.summary.calculated_at, timezone)}` : 'Расчёта нет'} ⓘ
        </Chip>
        {isFetching && <Chip>обновляется…</Chip>}
        <Spacer />
        <Button $variant="ghost" onClick={onRefetch} disabled={isFetching}>
          Обновить
        </Button>
        {d.summary && (
          <Button $variant="secondary" disabled={d.history_read_only} onClick={onExport}>
            Выгрузить
          </Button>
        )}
      </HeaderBar>
      <SectionNav aria-label="Разделы КСП">
        {SECTIONS.map((section) => (
          <SectionLink
            key={section.id}
            $active={section.id === active.id}
            $aside={section.aside}
            to={tabHref(params, section.tabs[0])}
          >
            {section.aside ? `⚙ ${section.title}` : section.title}
            {section.id === 'milestones' && openAlerts > 0 && (
              <Count $tone="bad" title="Открытые предупреждения о просрочке вех">
                {openAlerts}
              </Count>
            )}
          </SectionLink>
        ))}
      </SectionNav>
      {active.tabs.length > 1 && (
        <SubNav aria-label={active.title}>
          {active.tabs.map((id) => (
            <SubLink key={id} $active={tab === id} to={tabHref(params, id)}>
              {tabTitle(id)}
            </SubLink>
          ))}
        </SubNav>
      )}
      {!canWrite && (
        <Notice>
          Доступ для просмотра. Менять график, факт и вехи могут назначенные специалисты КСП.
        </Notice>
      )}
      {noGrants && (
        <Notice>
          Этот проект открыт у вас только для просмотра: права на изменения в КСП выдаются
          отдельно на каждый проект. Чтобы вести график, попросите права у того, кто ведёт
          этот проект в КСП.
        </Notice>
      )}
      {d.history_read_only && (
        <Notice>
          Исторический срез: настройки, исходные данные и расчёт сохранены в выбранной версии.
          Изменения доступны после возврата к текущим данным.
        </Notice>
      )}
      {d.historical_snapshot_missing && (
        <Notice>
          У выбранной версии нет сохранённого расчёта. Показаны утверждённые исходные данные;
          факт, прогноз и показатели этой версии недоступны.
        </Notice>
      )}
      {!!error && (
        <Notice $error>Связь с сервером прервалась — показаны последние полученные данные.</Notice>
      )}
      {message && <Notice>{message}</Notice>}
      {errors.length > 0 && (
        <Card>
          <h3>Не получилось выполнить действие</h3>
          {errors.map((issue, i) => (
            <Notice $error key={i}>
              {issue}
            </Notice>
          ))}
        </Card>
      )}
      {d.validation_issues?.length ? (
        <Card>
          <h3>Проверка исходных данных</h3>
          {d.validation_issues.map((issue, i) => (
            <Notice $error key={i}>
              {issue.message ?? issue.reason ?? label(issue.code)}
              {(issue.sheet || issue.cell) && ` · лист ${issue.sheet ?? '—'}, ячейка ${issue.cell ?? '—'}`}
              <Small>{issue.code}</Small>
            </Notice>
          ))}
        </Card>
      ) : null}
    </>
  );
}
